/**
 * Thin wrappers around PrimeReact 11's compound components so pages stay
 * readable. Each wrapper exposes a plain value/onChange API.
 */
import { useEffect, useState, type ChangeEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "./Button.js";
import { Checkbox } from "@primereact/ui/checkbox";
import { Dialog } from "@primereact/ui/dialog";
import { InputText } from "@primereact/ui/inputtext";
import { Select } from "@primereact/ui/select";
import { cx } from "./cx.js";

export interface Option<V extends string = string> {
  value: V;
  label: ReactNode;
}

export function SelectInput<V extends string>({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  ariaLabel,
  filter,
  className,
  id,
}: {
  value: V | "" | null | undefined;
  onChange: (value: V) => void;
  options: readonly Option<V>[];
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
  filter?: boolean;
  className?: string;
  id?: string;
}) {
  return (
    <Select.Root
      value={value ?? ""}
      onValueChange={(event: { value: unknown }) =>
        onChange((event.value ?? "") as V)
      }
      options={options as Option<V>[]}
      optionLabel="label"
      optionValue="value"
      disabled={disabled}
      fluid
      className={className}
    >
      <Select.Trigger type="button" id={id} aria-label={ariaLabel}>
        <Select.Value placeholder={placeholder} />
        <Select.Indicator className="pi pi-chevron-down" />
      </Select.Trigger>
      <Select.Portal>
        <Select.Positioner>
          <Select.Popup>
            {filter ? <Select.Filter /> : null}
            <Select.List />
          </Select.Popup>
        </Select.Positioner>
      </Select.Portal>
    </Select.Root>
  );
}

/** Local calendar date as YYYY-MM-DD. */
export function toDateValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Native date input: consistent value handling and the best mobile UX. */
export function DateInput({
  value,
  onChange,
  disabled,
  required,
  ariaLabel,
  minDate,
  maxDate,
  id,
  className = "w-full",
}: {
  value: string | null | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  ariaLabel?: string;
  minDate?: string;
  maxDate?: string;
  id?: string;
  className?: string;
}) {
  return (
    <InputText
      id={id}
      type="date"
      value={value ?? ""}
      min={minDate}
      max={maxDate}
      disabled={disabled}
      required={required}
      aria-label={ariaLabel}
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        onChange(event.target.value)
      }
      className={className}
    />
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  return (
    <Dialog.Root
      open={open}
      modal
      onOpenChange={(event: { value: boolean | undefined }) => {
        if (!event.value) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Popup
            className={cx(
              "w-[calc(100vw-2rem)]",
              wide ? "max-w-3xl" : "max-w-lg",
            )}
          >
            <Dialog.Header>
              <Dialog.Title>{title}</Dialog.Title>
              <Dialog.HeaderActions>
                <Dialog.Close className="pi pi-times" />
              </Dialog.HeaderActions>
            </Dialog.Header>
            <Dialog.Content className="space-y-4">{children}</Dialog.Content>
            {footer ? (
              <Dialog.Footer className="flex flex-wrap justify-end gap-2">
                {footer}
              </Dialog.Footer>
            ) : null}
          </Dialog.Popup>
        </Dialog.Positioner>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Whether a CSS media query matches; follows changes (rotation, resize). */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener("change", update);
    return () => list.removeEventListener("change", update);
  }, [query]);
  return matches;
}

export function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/**
 * List filters kept in the URL so views are shareable and survive reloads
 * (spec §10). Changing any filter resets the page to 1.
 */
export function useUrlFilters<K extends string>(keys: readonly K[]) {
  const [params, setParams] = useSearchParams();
  const values = Object.fromEntries(
    keys.map((key) => [key, params.get(key) ?? ""]),
  ) as Record<K, string>;
  const page = Math.max(1, Number(params.get("page")) || 1);
  const set = (patch: Partial<Record<K | "page", string | number>>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value === "" || value === undefined || value === null)
        next.delete(key);
      else next.set(key, String(value));
    }
    if (!("page" in patch)) next.delete("page");
    setParams(next, { replace: true });
  };
  return { values, page, set };
}

export function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const debounced = useDebounced(draft);
  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    if (debounced !== value) onChange(debounced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);
  const label = placeholder ?? t("common.search");
  return (
    <span className="relative block w-full sm:max-w-xs">
      <i
        className="pi pi-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-app-muted"
        aria-hidden="true"
      />
      <InputText
        value={draft}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          setDraft(event.target.value)
        }
        placeholder={label}
        aria-label={label}
        className="w-full"
        style={{ paddingLeft: "2.25rem" }}
        type="search"
      />
    </span>
  );
}

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
  /** Hide on the mobile card layout (secondary information). */
  hideOnMobile?: boolean;
  align?: "right";
}

/**
 * Server-paginated list: a table on wide screens, stacked cards on phones.
 * The first column is the row title and links to `href(row)`.
 */
export function DataList<T>({
  rows,
  columns,
  rowKey,
  href,
  empty,
  page,
  pageSize,
  total,
  onPage,
  loading,
}: {
  rows: readonly T[];
  columns: readonly Column<T>[];
  rowKey: (row: T) => string;
  href: (row: T) => string;
  empty: ReactNode;
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const [first, ...rest] = columns;
  return (
    <div
      className={cx(
        "overflow-hidden rounded-xl border border-app-border bg-app-surface",
        loading && "opacity-60",
      )}
    >
      {rows.length === 0 ? (
        <p className="px-4 py-12 text-center text-sm text-app-muted">{empty}</p>
      ) : (
        <>
          <table className="hidden w-full text-sm md:table">
            <thead className="border-b border-app-border bg-app-background text-left text-xs uppercase tracking-wide text-app-muted">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={cx(
                      "px-4 py-3 font-medium",
                      column.align === "right" && "text-right",
                      column.className,
                    )}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-app-border">
              {rows.map((row) => (
                <tr key={rowKey(row)} className="hover:bg-app-background">
                  {columns.map((column, index) => (
                    <td
                      key={column.key}
                      className={cx(
                        "px-4 py-3 align-middle",
                        column.align === "right" && "text-right tabular-nums",
                        column.className,
                      )}
                    >
                      {index === 0 ? (
                        <Link
                          to={href(row)}
                          className="font-medium text-brand hover:underline"
                        >
                          {column.cell(row)}
                        </Link>
                      ) : (
                        column.cell(row)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="divide-y divide-app-border md:hidden">
            {rows.map((row) => (
              <li key={rowKey(row)}>
                <Link
                  to={href(row)}
                  className="block px-4 py-3 active:bg-app-background"
                >
                  <div className="font-medium text-app-text">
                    {first!.cell(row)}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-app-muted">
                    {rest
                      .filter((column) => !column.hideOnMobile)
                      .map((column) => (
                        <span key={column.key}>{column.cell(row)}</span>
                      ))}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
      {pages > 1 ? (
        <div className="flex items-center justify-between gap-2 border-t border-app-border px-4 py-2 text-sm text-app-muted">
          <span>
            {t("common.pageOf", {
              page,
              pages,
              defaultValue: "Page {{page}} of {{pages}}",
            })}
          </span>
          <div className="flex gap-2">
            <Button
              size="small"
              variant="outlined"
              severity="secondary"
              disabled={page <= 1}
              onClick={() => onPage(page - 1)}
              aria-label={t("common.previous")}
              icon="pi pi-chevron-left"
            />
            <Button
              size="small"
              variant="outlined"
              severity="secondary"
              disabled={page >= pages}
              onClick={() => onPage(page + 1)}
              aria-label={t("common.next")}
              icon="pi pi-chevron-right"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Horizontal segmented filter (status tabs); scrolls on small screens. */
export function FilterTabs<V extends string>({
  value,
  onChange,
  options,
}: {
  value: V;
  onChange: (value: V) => void;
  options: readonly Option<V>[];
}) {
  return (
    <div
      role="tablist"
      className="-mx-4 flex gap-1 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={option.value === value}
          onClick={() => onChange(option.value)}
          className={cx(
            "shrink-0 rounded-full border px-3 py-1.5 text-sm transition-colors",
            option.value === value
              ? "border-brand bg-brand text-white"
              : "border-app-border bg-app-surface text-app-muted hover:text-app-text",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function CheckboxField({
  checked,
  onChange,
  label,
  hint,
  id,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  hint?: ReactNode;
  id: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start gap-2">
      <Checkbox.Root
        inputId={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(event: { checked: boolean }) =>
          onChange(Boolean(event.checked))
        }
      >
        <Checkbox.Box>
          <Checkbox.Indicator match="checked">
            <i className="pi pi-check" aria-hidden="true" />
          </Checkbox.Indicator>
        </Checkbox.Box>
      </Checkbox.Root>
      <label htmlFor={id} className="text-sm">
        <span className="font-medium text-app-text">{label}</span>
        {hint ? <span className="block text-app-muted">{hint}</span> : null}
      </label>
    </div>
  );
}
